/*
 * campaignContactFilter
 * ---------------------
 * Single source of truth for client-side campaign audience matching. Both the
 * live contact preview (CampaignContactsPanel) and the criteria chatbot's
 * recipient count (CampaignCriteriaChat) import from here, so the two views can
 * never drift apart (per the no-duplication rule).
 *
 * A campaign contact filter has exactly these fields:
 *   text       : string     free-text search (also accepted as `query`)
 *   categories : string[]   one or more contact types / categories (legacy
 *                           campaigns may still carry a single `category`)
 *   status     : string[]   one or more contact statuses
 *   mode     : "AND"|"OR" how the criteria combine
 *   cities   : string[]   city names
 *   zipcodes : string[]   5-digit zip codes
 *
 * `domain` is applied server-side, never here.
 */

export const valueText = (v) => String(v == null ? "" : v).trim().toLowerCase();

const toList = (value) =>
  (Array.isArray(value) ? value : String(value || "").split(/[,\n;]+/))
    .map((v) => String(v || "").trim())
    .filter(Boolean);

export const contactCategoryMatches = (contact, categories) => {
  const needles = toList(categories).map(valueText).filter(Boolean);
  if (!needles.length) return false;
  const needleSet = new Set(needles);
  return [
    contact?.category,
    contact?.details?.category,
    contact?.identity?.category,
    contact?.status?.category,
  ]
    .map(valueText)
    .some((v) => needleSet.has(v));
};

export const contactStatusMatches = (contact, status) => {
  const needles = (Array.isArray(status) ? status : [status]).map(valueText).filter(Boolean);
  if (!needles.length) return false;
  const needleSet = new Set(needles);
  return [
    contact?.status,
    contact?.status?.value,
    contact?.identity?.status,
    contact?.details?.status,
  ]
    .map(valueText)
    .some((v) => needleSet.has(v));
};

export const contactCityMatches = (contact, cities) => {
  const list = toList(cities);
  if (!list.length) return false;
  const city = valueText(contact?.identity?.address?.city || contact?.address?.city);
  if (!city) return false;
  return new Set(list.map(valueText)).has(city);
};

export const contactZipMatches = (contact, zipcodes) => {
  const list = toList(zipcodes);
  if (!list.length) return false;
  const zip = valueText(contact?.identity?.address?.zip || contact?.address?.zip).split("-")[0];
  if (!zip) return false;
  return new Set(list.map((z) => valueText(z).split("-")[0]).filter(Boolean)).has(zip);
};

export const contactTextMatches = (contact, text) => {
  const needle = valueText(text);
  if (!needle) return false;
  const hay = [
    contact?.name,
    contact?.identity?.firstName,
    contact?.identity?.lastName,
    contact?.identity?.company,
    contact?.identity?.person?.firstName,
    contact?.identity?.person?.lastName,
    contact?.identity?.company?.name,
    contact?.identity?.address?.street,
    contact?.identity?.address?.city,
    contact?.identity?.address?.zip,
    contact?.details?.notes,
  ]
    .map(valueText)
    .filter(Boolean)
    .join(" ");
  return hay.includes(needle);
};

/*
 * contactMatchesFilter — evaluate one contact against a campaign filter.
 * Accepts either `text` or `query` for the free-text field so both the panel
 * (`text`) and the chat's derived filter (`query`) can pass their native shape.
 */
export const contactMatchesFilter = (contact, filter) => {
  const f = filter || {};
  const text = f.text ?? f.query ?? "";
  const categories = toList(f.categories ?? f.category);
  const status = Array.isArray(f.status) ? f.status : toList(f.status);
  const checks = [];
  if (text) checks.push(contactTextMatches(contact, text));
  if (categories.length) checks.push(contactCategoryMatches(contact, categories));
  if (status.length) checks.push(contactStatusMatches(contact, status));
  if (toList(f.cities).length) checks.push(contactCityMatches(contact, f.cities));
  if (toList(f.zipcodes).length) checks.push(contactZipMatches(contact, f.zipcodes));
  if (!checks.length) return true;
  return String(f.mode || "AND").toUpperCase() === "OR"
    ? checks.some(Boolean)
    : checks.every(Boolean);
};
